// Nurture sequence state + API integration. Extracted from NurturePanel
// so the UI stays declarative and the state contract is unit-testable.
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

export function useNurtureSequence({ open, lead, onSequenceChange }) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [sequence, setSequence] = useState(null);
  const [emails, setEmails] = useState([]); // local editable copy
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open || !lead) return;
    setLoading(true);
    setDirty(false);
    api.get(`/leads/${lead.id}/nurture`)
      .then((r) => {
        setSequence(r.data.sequence || null);
        setEmails((r.data.sequence?.emails || []).map((e) => ({ ...e })));
      })
      .catch(() => toast.error("Failed to load nurture sequence"))
      .finally(() => setLoading(false));
  }, [open, lead]);

  const notifyParent = (seq) => {
    if (typeof onSequenceChange === "function") onSequenceChange(lead.id, seq);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/nurture/draft`);
      setSequence(data);
      setEmails((data.emails || []).map((e) => ({ ...e })));
      setDirty(false);
      notifyParent(data);
      toast.success("Draft generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { emails: emails.map((e) => ({ day: e.day, subject: e.subject, body: e.body })) };
      const { data } = await api.put(`/leads/${lead.id}/nurture`, payload);
      setSequence(data);
      setEmails((data.emails || []).map((e) => ({ ...e })));
      setDirty(false);
      notifyParent(data);
      toast.success("Edits saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const schedule = async () => {
    if (dirty) await save();
    setScheduling(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/nurture/schedule`);
      setSequence(data);
      setEmails((data.emails || []).map((e) => ({ ...e })));
      setDirty(false);
      notifyParent(data);
      toast.success("Sequence scheduled — Day 0 sent now");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Schedule failed");
    } finally {
      setScheduling(false);
    }
  };

  const cancel = async () => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/nurture/cancel`);
      setSequence(data);
      setEmails((data.emails || []).map((e) => ({ ...e })));
      setDirty(false);
      notifyParent(data);
      toast.success("Sequence cancelled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  const updateEmail = (idx, patch) => {
    setEmails((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    setDirty(true);
  };

  return {
    loading, generating, saving, scheduling,
    sequence, emails, dirty,
    generate, save, schedule, cancel, updateEmail,
  };
}
